import { doc, getFirestore, runTransaction, onSnapshot } from 'firebase/firestore';
import { cloudFunctionV2 } from '../api';
import { disqualifierPrompts } from '../../defaults';

export const callArvAgent = async ({
	companyId,
	propertyId,
	subjectProperty,
	allComps,
	propertyNotes = '',
	remodelLevel
}) => {
	const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
	const projectId = 'clozers';
	const region = 'us-central1';
	const emulatorUrl = `http://localhost:5001/${projectId}/${region}/arvAgent`;
	const productionUrl =
		process.env.REACT_APP_ARV_AGENT ||
		'https://arvagent-tilln6moiq-uc.a.run.app';
	const url = isLocalhost ? emulatorUrl : productionUrl;
	const params = {
		companyId,
		propertyId,
		subjectProperty,
		allComps,
		propertyNotes,
		remodelLevel,
		// On localhost, source comps from compFetcherV1 instead of the AWS comps
		// in `allComps`. arvAgent builds the Subject inline from subjectProperty
		// (needs uniqueIds.zillowId) — guard on it so a subject without a
		// zillowId gracefully falls back to the AWS comps instead of arvAgent
		// returning a hard 400. Off localhost this is always false (no change).
		useCompFetcherV1: isLocalhost && !!subjectProperty?.uniqueIds?.zillowId
	};

	let response = await cloudFunctionV2(url, params);
	if (isLocalhost && response.status !== 200 && url === emulatorUrl) {
		console.warn('[callArvAgent] Emulator call failed, falling back to production URL');
		response = await cloudFunctionV2(productionUrl, params);
	}
	return response;
};

export const rerunFromEvalDashboard = async ({
	runId,
	caseId,
	companyId,
	userId,
	refreshSubjectAnalysis = true
}) => {
	const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
	const projectId = 'clozers';
	const region = 'us-central1';
	const emulatorUrl = `http://localhost:5001/${projectId}/${region}/rerunFromEvalDashboard`;
	const productionUrl =
		process.env.REACT_APP_RERUN_FROM_EVAL_DASHBOARD ||
		'https://rerunfromevaldashboard-tilln6moiq-uc.a.run.app';
	const url = isLocalhost ? emulatorUrl : productionUrl;
	const params = {
		runId,
		caseId,
		companyId,
		userId,
		refreshSubjectAnalysis
	};

	let response = await cloudFunctionV2(url, params);
	if (isLocalhost && response.status !== 200 && url === emulatorUrl) {
		console.warn('[rerunFromEvalDashboard] Emulator call failed, falling back to production URL');
		response = await cloudFunctionV2(productionUrl, params);
	}
	return response;
};

export const deleteEvalRunFromDashboard = async ({
	runId,
	companyId,
	userId
}) => {
	const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
	const projectId = 'clozers';
	const region = 'us-central1';
	const emulatorUrl = `http://localhost:5001/${projectId}/${region}/deleteEvalRunFromDashboard`;
	const productionUrl =
		process.env.REACT_APP_DELETE_EVAL_RUN_FROM_DASHBOARD ||
		'https://deleteevalrunfromdashboard-tilln6moiq-uc.a.run.app';
	const url = isLocalhost ? emulatorUrl : productionUrl;
	const params = {
		runId,
		companyId,
		userId
	};

	let response = await cloudFunctionV2(url, params);
	const shouldFallbackToProd =
		isLocalhost &&
		url === emulatorUrl &&
		[406, 404, 500, 502, 503, 504].includes(response?.status);

	if (shouldFallbackToProd) {
		console.warn('[deleteEvalRunFromDashboard] Emulator unavailable, falling back to production URL');
		response = await cloudFunctionV2(productionUrl, params);
	}

	return response;
};

export const setPropertyARVData = async (colRef, docRef, subColRef, subDocRef, arvData, arvMessages) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				data.arvData = arvData;
				data.arvMessages = arvMessages;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const cleanGPTMessages = (arvMessages) => {
	const cleanedMessages = arvMessages
		.filter(message => message.role !== "user_chat")
		.filter(message => message.role !== "event")
		.filter(message => message.role !== "action_required")
		.map(({ role, content }) => ({ role, content }));
	return cleanedMessages;
};

export const queryARVChat = async(arvMessages) => {
	const gptURL = process.env.REACT_APP_CLOZERS_GPT;
	const cleanedMessages = await cleanGPTMessages(arvMessages);

	return new Promise(async resolve => {
		const params = {
            service: "openai",
            messages: cleanedMessages,
            model: "o3",
			responseFormat: {
				type: "json_object"
			}
        };
		const queryGPTData = await cloudFunctionV2(gptURL, params);
		if ( queryGPTData.status === 200 ) {
			const res = {
				status: 200,
				data: queryGPTData.data
			}
			resolve(res);
		}
		else {
			const res = {
				status: 400,
				error: queryGPTData.error
			};
			resolve(res);
		}
	});
};

export const setPropertyARVMessages = async(colRef, docRef, subColRef, subDocRef, arvMessages, arvData, actionRequired) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;

				if ( arvData.arvRegeneration === true && data.arvData !== undefined ) {
					data.arvData.arv = arvData.arv;
					data.arvData.arvRange = arvData.arvRange;
					data.arvData.description = arvData.description;
					if ( arvData.comps !== undefined ) {
						data.arvData.comps = arvData.comps;
					}
				}

				if ( actionRequired === true && data.arvData.scenarios !== undefined && data.arvData.scenarios !== null ) {
					if ( arvData.arv === data.arvData.scenarios.optimistic.arv ) {
						data.arvData.scenarios.optimistic.selected = true;

						const newArvMessage = {
							role: "event",
							content: "Optimistic strategy selected",
							date: new Date()
						};
						arvMessages.push(newArvMessage);
					}
					else if ( arvData.arv === data.arvData.scenarios.conservative.arv ) {
						data.arvData.scenarios.conservative.selected = true;

						const newArvMessage = {
							role: "event",
							content: "Conservative strategy selected",
							date: new Date()
						};
						arvMessages.push(newArvMessage);
					}
				}

				data.arvMessages = arvMessages;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const selectARVScenario = async(colRef, docRef, subColRef, subDocRef, arvMessages, arvData, optimistic) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				data.arvMessages = arvMessages;
				data.arvData.arv = arvData.arv;
				data.arvData.arvRange = arvData.arvRange;
				data.arvData.description = arvData.description;

				if ( optimistic ) {
					data.arvData.scenarios.optimistic.selected = true;
				}
				else {
					data.arvData.scenarios.conservative.selected = true;
				}

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const updatePropertyARVMessages = async(colRef, docRef, subColRef, subDocRef, arvMessages) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				data.arvMessages = arvMessages;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const queryDisqualifyingStatement = async(subjectPropertySubmission, bestCompSubmission, propertySubmission) => {
	const url = process.env.REACT_APP_CLOZERS_GPT;
	const promptPayload = disqualifierPrompts.initial({
		comps: bestCompSubmission,
		subjectProperty: subjectPropertySubmission,
		selectedComp: propertySubmission
	})
	const prompt = promptPayload.prompt;
	const systemInstructions = promptPayload.system;
	const parts = [
		{
			text: prompt
		}
	];

	const newElement = {
		role: "user",
		parts: parts
	};
	const contents = [newElement];

	const params = {
		service: "gemini",
		messages: [
			{
				role: "user",
				content: prompt
			}
		],
		model: "gemini-2.0-flash",
		modelOptions: {
			model: "gemini-2.0-flash",
			systemInstruction: systemInstructions
		},
		contents: {
			contents: contents
		}
	};

	return new Promise(async resolve => {
		const queryData = await cloudFunctionV2(url, params);
		resolve(queryData);
	});
};

export const listenForFastChatbot = async (
    url,
    params,
    setArvMessages,
    setArvEstimate,
    setArvDescription,
    setArvRange,
    setArvReasoning,
    setChatbotLoading,
    setTypeOutText,
    setActionRequired,
    setTempStatus
) => {
    const testMode = window.location.hostname === "localhost";
    if (testMode) console.log('🚀 Starting FastChatbot SSE connection...');
    
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ data: params })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        if (testMode) console.log('📡 SSE connection established');

        while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
                if (testMode) console.log('✅ SSE stream completed');
                setChatbotLoading(false);
                break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    try {
                        const eventData = JSON.parse(line.slice(6));
                        if (testMode) console.log('📨 SSE Event:', eventData.type, eventData.data);
                        
                        switch (eventData.type) {
                            case 'thinking':
                                if (testMode) console.log('💭 Thinking:', eventData.data.message);
                                if (typeof setTempStatus === 'function') setTempStatus(eventData.data.message);
                                break;
                                
                            case 'progress':
                                if (testMode) console.log('📊 Progress:', eventData.data.message);
                                if (typeof setTempStatus === 'function') setTempStatus(eventData.data.message);
                                break;
                                
                            case 'search_results':
                                if (testMode) console.log('🔍 Search Results:', eventData.data);
                                // Handle search results
                                if (eventData.data.count === 0) {
                                    const noResultsMessage = {
                                        role: 'assistant',
                                        content: `I searched for properties matching your criteria but didn't find any matching comps in our database. This could mean:\n\n- No properties in that area have sold recently\n- The search criteria might need adjustment\n- Properties might be listed under different names\n\nWould you like me to search for similar properties in nearby areas?`,
                                        date: new Date().toISOString()
                                    };
                                    setArvMessages(prev => [...prev, noResultsMessage]);
                                } else {
                                    const resultsMessage = {
                                        role: 'assistant',
                                        content: `I found ${eventData.data.count} properties matching your criteria:\n\n${eventData.data.comps.map((comp, i) => `${i + 1}. [zpid_${comp.zpid}] - $${comp.price?.toLocaleString() || 'Price N/A'}`).join('\n')}`,
                                        date: new Date().toISOString()
                                    };
                                    setArvMessages(prev => [...prev, resultsMessage]);
                                }
                                if (typeof setTempStatus === 'function') setTempStatus(null);
                                break;
                                
                            case 'arv_response':
                                if (testMode) console.log('💰 ARV Response:', eventData.data);
                                if (eventData.data.arv) {
                                    setArvEstimate(eventData.data.arv);
                                    setArvRange(eventData.data.arvRange);
                                    setArvDescription(eventData.data.analysis);
                                    setArvReasoning(eventData.data.reasoning);
                                    
                                    // Add event and assistant messages to arvMessages
                                    setArvMessages(prev => {
                                        const updated = [...prev];
                                        
                                        // Add "ARV Estimate Regenerated" event if not already present
                                        const lastMessage = updated[updated.length - 1];
                                        if (!lastMessage || lastMessage.role !== 'event' || lastMessage.content !== 'ARV Estimate Regenerated') {
                                            updated.push({
                                                role: 'event',
                                                content: 'ARV Estimate Regenerated',
                                                date: new Date().toISOString()
                                            });
                                        }
                                        
                                        // Add assistant message with ARV analysis
                                        if (eventData.data.analysis) {
                                            updated.push({
                                                role: 'assistant',
                                                content: eventData.data.analysis,
                                                date: new Date().toISOString()
                                            });
                                        }
                                        
                                        // Add adjustments message
                                        if (eventData.data.comp_adjustments) {
                                            updated.push({
                                                role: 'adjustments',
                                                content: eventData.data.comp_adjustments,
                                                date: new Date().toISOString()
                                            });
                                        }
                                        
                                        return updated;
                                    });
                                }
                                if (typeof setTempStatus === 'function') setTempStatus(null);
                                break;
                                
                            case 'answer_response':
                                if (testMode) console.log('❓ Answer Response:', eventData.data);
                                const answerMessage = {
                                    role: 'assistant',
                                    content: eventData.data.analysis,
                                    date: new Date().toISOString()
                                };
                                setArvMessages(prev => [...prev, answerMessage]);
                                if (typeof setTempStatus === 'function') setTempStatus(null);
                                break;
                                
                            case 'streaming_text':
                                if (testMode) console.log('📝 Streaming text:', eventData.data.content);
                                // Handle streaming text if needed
                                break;
                                
                            case 'error':
                                if (testMode) console.error('❌ SSE Error:', eventData.data.message);
                                const errorMessage = {
                                    role: 'assistant',
                                    content: `I encountered an error: ${eventData.data.message}`,
                                    date: new Date().toISOString()
                                };
                                setArvMessages(prev => [...prev, errorMessage]);
                                if (typeof setTempStatus === 'function') setTempStatus(null);
                                break;
                                
                            case 'complete':
                                if (testMode) console.log('✅ SSE Complete:', eventData.data);
                                setChatbotLoading(false);
                                if (typeof setTempStatus === 'function') setTempStatus(null);
                                break;
                                
                            default:
                                if (testMode) console.log('❓ Unknown SSE event:', eventData.type);
                        }
                    } catch (parseError) {
                        if (testMode) console.error('❌ SSE Parse Error:', parseError, 'Line:', line);
                    }
                }
            }
        }
    } catch (error) {
        if (testMode) console.error('❌ FastChatbot SSE Error:', error);
        setChatbotLoading(false);
        
        const errorMessage = {
            role: 'assistant',
            content: `I encountered an error while processing your request: ${error.message}`,
            date: new Date().toISOString()
        };
        setArvMessages(prev => [...prev, errorMessage]);
    }
};

export const listenForARVMessages = async(
	companyId,
	propertyId,
	setArvMessages,
	setArvEstimate,
	setArvDescription,
	setArvRange,
	url,
	params,
	oldArvMessages,
    oldArvEstimate,
	allComps,
	viewComp,
	setTypeOutText,
	setArvReasoning,
	setChatbotLoading,
	setActionRequired
) => {
	// ---- one‑off listener while the ARV Cloud Function runs ----
	const db = getFirestore();
	const ref = doc(
		db,
		"companies",
		companyId,
		"properties",
		propertyId
	);
	// listener ends after two chat updates OR one arv update (whichever happens first)
	let chatUpdateCount = 0;
	let latestMsgLength = oldArvMessages.length;
	let arvUpdated = false;

	const unsubscribeArvListener = onSnapshot(ref, (snap) => {
        const docData = snap.data().data;

		/* ---- diff‑aware updates ---- */
		// Stream chat updates only if they changed
		if (
			docData.arvMessages &&
			Array.isArray(docData.arvMessages) &&
			typeof setArvMessages === "function"
		) {
			const sameMessages =
				JSON.stringify(docData.arvMessages) ===
				JSON.stringify(oldArvMessages);
			if ( (docData.arvMessages.length > latestMsgLength) && !sameMessages ) {
				setArvMessages(docData.arvMessages);
				setTypeOutText(true);

				chatUpdateCount++;
				latestMsgLength = docData.arvMessages.length;
			}
		}

		// Stream final ARV figures only if the ARV changed
		if (
			docData.arvData &&
			docData.arvData.arv !== undefined &&
			docData.arvData.arv !== null &&
			typeof setArvEstimate === "function"
		) {
			if (docData.arvData.arv !== oldArvEstimate) {
				setArvEstimate(docData.arvData.arv);
				setArvRange(docData.arvData.arvRange);
				setArvReasoning(docData.arvData.reasoning);

				arvUpdated = true;
			}
		}

		if (arvUpdated || chatUpdateCount >= 1) {
			unsubscribeArvListener();
			setChatbotLoading(false);
			setActionRequired(false);
		}
	});
	cloudFunctionV2(url, params);
}

export const listenForARVData = async(
	companyId,
	propertyId,
	setArvMessages,
	setArvEstimate,
	setArvRange,
	setArvReasoning,
	setChatbotLoading,
	setTypeOutText
) => {
	// ---- one‑off listener while the ARV Cloud Function runs ----
	const db = getFirestore();
	const ref = doc(
		db,
		"companies",
		companyId,
		"properties",
		propertyId
	);
	// listener ends after two chat updates OR one arv update (whichever happens first)

	const unsubscribeArvListener = onSnapshot(ref, (snap) => {
        const docData = snap.data().data;

		/* ---- diff‑aware updates ---- */
		// Stream chat updates only if they changed

		// Stream final ARV figures only if the ARV changed
		if (
			docData.arvData &&
			docData.arvData.arv !== undefined &&
			docData.arvData.arv !== null &&
			typeof setArvEstimate === "function"
		) {
			if ( docData.arvData.arv ) {
				setArvEstimate(docData.arvData.arv);
				setArvRange(docData.arvData.arvRange);
				setArvReasoning(docData.arvData.reasoning);
				setArvMessages(docData.arvMessages);

				unsubscribeArvListener();
				setChatbotLoading(false);

				setTimeout(() => {
					setTypeOutText(false);
				}, 10000);
			}
		}
	});
};

export const writeArvRequested = async(companyId, propertyId) => {
	const db = getFirestore();
	const queryParams = doc(db, "companies", companyId, "properties", propertyId);
	return new Promise(async resolve => {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				resolve({
					status: 400,
					error: "Document does not exist"
				});
			}
			else {
				const data = query.data().data;
				data.arvRequested = true;
				transaction.update(queryParams, {
					data
				});
				resolve({
					status: 200
				});
			}
		});
	});
};
